import { cloneDeep } from 'lodash';
import {
  CONFIG_STATUS_LEVEL,
  CONFIG_TYPES,
  CRM_DEDUPLICATION_TEMPLATE,
  CRM_FIELD_MAPPING_TEMPLATE,
  CRM_PARENT_LINKING_TEMPLATE,
  // CRM_PARENT_LINKING_TEMPLATE,
  ENTITY_LEVEL,
  SIDE_NAV_BAR_OPTIONS,
  SYNC_DIRECTION,
} from '../../../../common/constants/integrationConstants/crmConstants';
import {
  findDataMappingStatus,
  transformEntityMappings,
} from '../../../../common/util/crmIntegrationUtils/crmUtils';
import {
  deepCloneTemplate,
  getAddDataMapRowState,
  getChangeDataMappingState,
  getChangeDataMappingwithFieldConfigState,
} from '../../../../common/util/reducerUtils/CrmReducerUtils';
import {
  addDataMapRow,
  cancelDataMapping,
  cancelDeduplicationMapping,
  changeCrmDataMapping,
  changeCrmDataMappingFieldConfig,
  changeDeduplication,
  clearUnsavedChanges,
  createDataMapping,
  createDeduplicationMapping,
  fetchCrmDataMappings,
  fetchCrmDeduplicationMappings,
  changeParentLinkMapping,
  cancelParentLinkMapping,
  removeDataMapRow,
  changeAdditionalDeduplicationMapping,
  createAdditionalDeduplicationMapping,
  cancelAdditionalDeduplicationMapping,
  fetchAdditionalDeduplicationMapping,
} from '../../../routines/integrations/crmRoutines';

const initialState = {
  [CONFIG_TYPES.DEDUPLICATION]: {
    organization: deepCloneTemplate(CRM_DEDUPLICATION_TEMPLATE),
    account: deepCloneTemplate(CRM_DEDUPLICATION_TEMPLATE),
    contact: deepCloneTemplate(CRM_DEDUPLICATION_TEMPLATE),
    opportunity: deepCloneTemplate(CRM_DEDUPLICATION_TEMPLATE),
    risk: deepCloneTemplate(CRM_DEDUPLICATION_TEMPLATE),
  },
  [CONFIG_TYPES.DAILY_SYNC_RECORD_ID_MAPPING]: {
    organization: deepCloneTemplate(CRM_DEDUPLICATION_TEMPLATE),
    account: deepCloneTemplate(CRM_DEDUPLICATION_TEMPLATE),
  },
  [CONFIG_TYPES.IN_SYNC]: {
    organization: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
    account: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
  },
  [CONFIG_TYPES.PARENT_LINK]: {
    account: deepCloneTemplate(CRM_PARENT_LINKING_TEMPLATE),
  },
  [CONFIG_TYPES.DATA_MAPPING]: {
    into: {
      // CRM to Velaris Sync
      organization: {
        create: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
        update: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
        churn: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
        archive: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
      },
      account: {
        create: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
        update: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
        churn: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
        archive: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
      },
      contact: {
        create: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
      },
      opportunity: {
        create: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
      },
      risk: {
        create: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
      },
    },
    out: {
      // Velaris to CRM sync
      organization: {
        create: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
        update: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
        churn: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
        archive: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
      },
      account: {
        create: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
        update: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
        churn: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
        archive: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
      },
      contact: {
        create: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
        update: deepCloneTemplate(CRM_FIELD_MAPPING_TEMPLATE),
      },
    },
  },
  persistedStateDataMapping: null,
  persistedStateInSyncDataMapping: null,
  persistedStateParentLinkDeduplication: null,
  parentLinkInitialStatus: null,
  hasUnSavedConfig: false,
  deduplicationObjTypeChanged: false,
  loading: false,
  error: null,
};

const dataMappingDestructure = (updatedInto) => {
  const updatedIntoMappings = Object.entries(updatedInto).reduce(
    (acc, [entityKey, entityValue]) => {
      const updatedActionTypes = Object.entries(entityValue).reduce(
        (innerAcc, [actionTypeKey, actionTypeValue]) => {
          const updatedDataMappings = Object.entries(
            actionTypeValue.dataMappings
          ).reduce((dataMappingsAcc, [mappingKey, mappingValue]) => {
            const { dataMappingId, hubspotDataMappingTypeId, velaris, crm } =
              mappingValue;
            const crmPropertyType = crm?.propertyType;

            const updatedMapping = {
              dataMappingId,
              hubspotDataMappingTypeId,
              velaris: { fieldId: velaris.fieldId },
              crm: { fieldId: crm.fieldId },
              crmPropertyType: { fieldId: crmPropertyType },
              fieldConfig: crm?.fieldConfig,
            };

            return {
              ...dataMappingsAcc,
              [mappingKey]: updatedMapping,
            };
          }, {});

          return {
            ...innerAcc,
            [actionTypeKey]: {
              ...actionTypeValue,
              dataMappings: updatedDataMappings,
            },
          };
        },
        {}
      );

      return {
        ...acc,
        [entityKey]: updatedActionTypes,
      };
    },
    {}
  );
  return updatedIntoMappings;
};

const crmDataMappingReducer = (state = initialState, action) => {
  switch (action.type) {
    case addDataMapRow.TRIGGER: {
      const {
        entityLevel,
        criteriaType,
        dataMappingId,
        syncDirection,
        mappingType,
      } = action.payload;
      const newDataMappingState = getAddDataMapRowState(
        state,
        entityLevel,
        criteriaType,
        dataMappingId,
        syncDirection,
        mappingType
      );
      return {
        ...state,
        [mappingType]: newDataMappingState,
        hasUnSavedConfig: true,
      };
    }
    case changeCrmDataMapping.TRIGGER: {
      const {
        entityLevel,
        criteriaType,
        syncDirection,
        dataMappingId,
        side,
        propertyId,
        mappingType,
      } = action.payload;
      const newDataMappingState = getChangeDataMappingState({
        state,
        entityLevel,
        criteriaType,
        syncDirection,
        dataMappingId,
        side,
        propertyId,
        mappingType,
      });
      return {
        ...state,
        [mappingType]: newDataMappingState,
        hasUnSavedConfig: true,
      };
    }
    case changeCrmDataMappingFieldConfig.TRIGGER: {
      const {
        entityLevel,
        criteriaType,
        syncDirection,
        dataMappingId,
        mappingType,
        fieldConfig,
      } = action.payload;
      const newDataMappingState = getChangeDataMappingwithFieldConfigState({
        state,
        entityLevel,
        criteriaType,
        syncDirection,
        dataMappingId,
        mappingType,
        fieldConfig,
      });
      return {
        ...state,
        [mappingType]: newDataMappingState,
        hasUnSavedConfig: true,
      };
    }
    case removeDataMapRow.TRIGGER: {
      const {
        entityLevel,
        criteriaType,
        dataMappingId,
        syncDirection,
        mappingType,
      } = action.payload;
      if (mappingType === CONFIG_TYPES.DATA_MAPPING) {
        const newDataMappingState = {
          ...state[CONFIG_TYPES.DATA_MAPPING],
          [syncDirection]: {
            ...state[CONFIG_TYPES.DATA_MAPPING][syncDirection],
            [entityLevel]: {
              ...state[CONFIG_TYPES.DATA_MAPPING][syncDirection][entityLevel],
              [criteriaType]: {
                ...state[CONFIG_TYPES.DATA_MAPPING][syncDirection][entityLevel][
                  criteriaType
                ],
                dataMappings: {
                  ...state[CONFIG_TYPES.DATA_MAPPING][syncDirection][
                    entityLevel
                  ][criteriaType].dataMappings,
                },
              },
            },
          },
        };
        delete newDataMappingState[syncDirection][entityLevel][criteriaType]
          .dataMappings[dataMappingId];
        newDataMappingState[syncDirection][entityLevel][
          criteriaType
        ].dataMappings = Object.values(
          newDataMappingState[syncDirection][entityLevel][criteriaType]
            .dataMappings
        ).reduce((map, dataMapping, index) => {
          map[index] = { ...dataMapping, dataMappingId: index };
          return map;
        }, {});
        const newDataMappingStatus = findDataMappingStatus(
          newDataMappingState[syncDirection][entityLevel][criteriaType]
            .dataMappings
        );
        newDataMappingState[syncDirection][entityLevel][criteriaType].status =
          newDataMappingStatus;
        return {
          ...state,
          [CONFIG_TYPES.DATA_MAPPING]: newDataMappingState,
          hasUnSavedConfig: true,
        };
      }
      const newInSyncDataMappingState = {
        ...state[mappingType],
        [entityLevel]: {
          ...state[mappingType][entityLevel],
          dataMappings: {
            ...state[mappingType][entityLevel].dataMappings,
          },
        },
      };
      delete newInSyncDataMappingState[entityLevel].dataMappings[dataMappingId];
      newInSyncDataMappingState[entityLevel].dataMappings = Object.values(
        newInSyncDataMappingState[entityLevel].dataMappings
      ).reduce((map, dataMapping, index) => {
        map[index] = { ...dataMapping, dataMappingId: index };
        return map;
      }, {});

      const newInSyncataMappingStatus = findDataMappingStatus(
        newInSyncDataMappingState[entityLevel].dataMappings
      );
      newInSyncDataMappingState[entityLevel].status = newInSyncataMappingStatus;

      return {
        ...state,
        [CONFIG_TYPES.IN_SYNC]: newInSyncDataMappingState,
        hasUnSavedConfig: true,
      };
    }

    case createDataMapping.TRIGGER:
      return {
        ...state,
        error: null,
      };
    case fetchCrmDataMappings.TRIGGER:
      return {
        ...state,
        loading: true,
        error: false,
      };
    case fetchCrmDataMappings.SUCCESS: {
      const { mappingType, inSync, into, out, customParentLinkDeduplication } =
        action.payload;

      if (mappingType === CONFIG_TYPES.DATA_MAPPING) {
        const newDataMappingState = {
          ...state[CONFIG_TYPES.DATA_MAPPING],
          [SYNC_DIRECTION.OUT]: dataMappingDestructure(out),
          [SYNC_DIRECTION.IN]: dataMappingDestructure(into),
        };
        return {
          ...state,
          [CONFIG_TYPES.DATA_MAPPING]: newDataMappingState,
          persistedStateDataMapping: newDataMappingState,
          loading: false,
          error: false,
          hasUnSavedConfig: false,
        };
      }
      if (mappingType === CONFIG_TYPES.PARENT_LINK) {
        const transformedData = transformEntityMappings(
          customParentLinkDeduplication,
          true
        );
        return {
          ...state,
          [CONFIG_TYPES.PARENT_LINK]: transformedData,
          persistedStateParentLinkDeduplication: transformedData,
          parentLinkInitialStatus: customParentLinkDeduplication.account.status,
          loading: false,
          error: false,
          hasUnSavedConfig: false,
        };
      }
      const inSyncState = Object.entries(inSync).reduce(
        (acc, [entity, entityObj]) => {
          const updatedDataMappings = Object.entries(
            entityObj.dataMappings
          ).reduce((dataMappingsAcc, [mappingKey, mappingValue]) => {
            const { dataMappingId, hubspotDataMappingTypeId, velaris, crm } =
              mappingValue;
            const crmPropertyType = crm?.propertyType;
            const transformedMapping = {
              dataMappingId,
              hubspotDataMappingTypeId,
              velaris: { fieldId: velaris.fieldId },
              crm: { fieldId: crm.fieldId },
              crmPropertyType: { fieldId: crmPropertyType },
              fieldConfig: crm?.fieldConfig,
            };

            return {
              ...dataMappingsAcc,
              [mappingKey]: transformedMapping,
            };
          }, {});

          const transformedEntity = {
            dataMappings: updatedDataMappings,
            status: entityObj.status,
          };

          return {
            ...acc,
            [entity]: transformedEntity,
          };
        },
        {}
      );

      return {
        ...state,
        [CONFIG_TYPES.IN_SYNC]: inSyncState,
        persistedStateInSyncDataMapping: inSyncState,
        loading: false,
        error: false,
      };
    }
    case createDataMapping.SUCCESS: {
      const {
        entity: entityLevel,
        criteria,
        mapping,
        status,
        syncDirection,
        mappingType,
        type,
      } = action.payload;
      if (mappingType === CONFIG_TYPES.DATA_MAPPING) {
        const newDataMappingState = {
          ...state[CONFIG_TYPES.DATA_MAPPING],
          [syncDirection]: {
            ...state[CONFIG_TYPES.DATA_MAPPING][syncDirection],
            [entityLevel]: {
              ...state[CONFIG_TYPES.DATA_MAPPING][syncDirection][entityLevel],
              [criteria]: {
                dataMappings: mapping.reduce(
                  (dataMappingMap, mappingRow, index) => {
                    dataMappingMap[index] = mappingRow;
                    return dataMappingMap;
                  },
                  {}
                ),
                status,
              },
            },
          },
        };
        return {
          ...state,
          [CONFIG_TYPES.DATA_MAPPING]: newDataMappingState,
          persistedStateDataMapping: newDataMappingState,
          loading: false,
          error: false,
          hasUnSavedConfig: false,
        };
      }
      if (mappingType === CONFIG_TYPES.PARENT_LINK) {
        const parentLinkConfig = { ...state[CONFIG_TYPES.PARENT_LINK] };
        parentLinkConfig[entityLevel] = {
          criteria,
          velaris: mapping[0].velaris,
          crm: mapping[0].crm,
          crmPropertyType: mapping[0].crmPropertyType,
          type,
          status,
        };
        return {
          ...state,
          [CONFIG_TYPES.PARENT_LINK]: parentLinkConfig,
          persistedStateParentLinkDeduplication: parentLinkConfig,
          loading: false,
          error: false,
          hasUnSavedConfig: false,
        };
      }
      const newInSyncDataMappingState = {
        ...state[CONFIG_TYPES.IN_SYNC],
        [entityLevel]: {
          ...state[CONFIG_TYPES.IN_SYNC][entityLevel],
          dataMappings: mapping.reduce((dataMappingMap, mappingRow, index) => {
            dataMappingMap[index] = mappingRow;
            return dataMappingMap;
          }, {}),
          status,
        },
      };
      return {
        ...state,
        [CONFIG_TYPES.IN_SYNC]: newInSyncDataMappingState,
        persistedStateInSyncDataMapping: newInSyncDataMappingState,
        loading: false,
        error: false,
        hasUnSavedConfig: false,
      };
    }
    case createDataMapping.FAILURE:
    case createDeduplicationMapping.FAILURE:
    case createAdditionalDeduplicationMapping.FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
        hasUnSavedConfig: false,
      };
    case cancelDataMapping.TRIGGER: {
      const { mappingType } = action.payload;
      return {
        ...state,
        [mappingType]: cloneDeep(
          mappingType === CONFIG_TYPES.IN_SYNC
            ? state.persistedStateInSyncDataMapping
            : state.persistedStateDataMapping
        ),
        hasUnSavedConfig: false,
      };
    }
    case clearUnsavedChanges.TRIGGER: {
      const { currentConfigType, entityTab, sideNavBarTab } = action.payload;

      const { hasUnSavedConfig } = { ...state };
      if (!hasUnSavedConfig) {
        return {
          ...state,
        };
      }
      if (currentConfigType === CONFIG_TYPES.DEDUPLICATION) {
        return {
          ...state,
          [CONFIG_TYPES.DEDUPLICATION]: cloneDeep(
            state.persistedStateDeduplication
          ),
          hasUnSavedConfig: false,
        };
      }
      if (currentConfigType === CONFIG_TYPES.DAILY_SYNC_RECORD_ID_MAPPING) {
        return {
          ...state,
          [CONFIG_TYPES.DEDUPLICATION]: cloneDeep(
            state.persistedStateRecordIdMapping
          ),
          hasUnSavedConfig: false,
        };
      }
      if (sideNavBarTab === SIDE_NAV_BAR_OPTIONS.PARENT_LINK) {
        return {
          ...state,
          [CONFIG_TYPES.PARENT_LINK]: cloneDeep(
            state.persistedStateParentLinkDeduplication
          ),
          hasUnSavedConfig: false,
        };
      }
      if (
        entityTab === ENTITY_LEVEL.CONTACT &&
        currentConfigType !== CONFIG_TYPES.DEDUPLICATION &&
        sideNavBarTab !== SIDE_NAV_BAR_OPTIONS.IN_SYNC
      ) {
        return {
          ...state,
          [CONFIG_TYPES.DATA_MAPPING]: cloneDeep(
            state.persistedStateDataMapping
          ),
          hasUnSavedConfig: false,
        };
      }
      if (sideNavBarTab === SIDE_NAV_BAR_OPTIONS.IN_SYNC) {
        return {
          ...state,
          [CONFIG_TYPES.IN_SYNC]: cloneDeep(
            state.persistedStateInSyncDataMapping
          ),
          hasUnSavedConfig: false,
        };
      }
      return {
        ...state,
        [CONFIG_TYPES.DATA_MAPPING]: cloneDeep(state.persistedStateDataMapping),
        hasUnSavedConfig: false,
      };
    }
    // DE DUPLICATON ACTIONS
    case changeDeduplication.TRIGGER: {
      const { entityLevel, side, propertyId } = action.payload;
      const newDeduplicationState = {
        ...state[CONFIG_TYPES.DEDUPLICATION],
        [entityLevel]: {
          ...state[CONFIG_TYPES.DEDUPLICATION][entityLevel],
          [side]: { fieldId: propertyId },
        },
      };
      const status =
        newDeduplicationState[entityLevel].velaris.fieldId &&
        newDeduplicationState[entityLevel].crm.fieldId
          ? CONFIG_STATUS_LEVEL.COMPLETED
          : CONFIG_STATUS_LEVEL.IN_PROGRESS;
      newDeduplicationState[entityLevel].status = status;
      return {
        ...state,
        [CONFIG_TYPES.DEDUPLICATION]: newDeduplicationState,
        hasUnSavedConfig: true,
      };
    }
    case createDeduplicationMapping.TRIGGER:
    case createAdditionalDeduplicationMapping.TRIGGER:
      return {
        ...state,
        error: false,
      };
    case cancelDeduplicationMapping.TRIGGER: {
      const previousDeduplicationMapping = cloneDeep(
        state.persistedStateDeduplication
      );
      return {
        ...state,
        [CONFIG_TYPES.DEDUPLICATION]: previousDeduplicationMapping,
        hasUnSavedConfig: false,
      };
    }
    case fetchCrmDeduplicationMappings.SUCCESS: {
      const transformedDeduplication = transformEntityMappings(action.payload);
      return {
        ...state,
        [CONFIG_TYPES.DEDUPLICATION]: transformedDeduplication,
        persistedStateDeduplication: transformedDeduplication,
        loading: false,
        error: false,
        hasUnSavedConfig: false,
      };
    }

    case createDeduplicationMapping.SUCCESS: {
      const {
        entity: entityLevel,
        mapping: [{ velaris, crm }],
      } = action.payload;
      const deduplicationConfig = { ...state[CONFIG_TYPES.DEDUPLICATION] };
      deduplicationConfig[entityLevel] = {
        velaris,
        crm: { fieldId: crm.fieldId },
        crmPropertyType: { fieldId: crm.propertyType },
        status: CONFIG_STATUS_LEVEL.SAVED,
      };
      return {
        ...state,
        [CONFIG_TYPES.DEDUPLICATION]: deduplicationConfig,
        persistedStateDeduplication: deduplicationConfig,
        loading: false,
        error: false,
        hasUnSavedConfig: false,
      };
    }

    // Parent Linking Actions
    case changeParentLinkMapping.TRIGGER: {
      const { entityLevel, side, propertyId, type, crmPropertyType } =
        action.payload;
      const newParentLinkDeduplicationState = {
        ...state[CONFIG_TYPES.PARENT_LINK],
        account: {
          ...state[CONFIG_TYPES.PARENT_LINK][entityLevel],
          ...(side !== undefined ? { [side]: { fieldId: propertyId } } : {}),
          ...(type !== undefined ? { type } : {}),
          ...(crmPropertyType !== undefined
            ? { crmPropertyType: { fieldId: crmPropertyType } }
            : {}),
        },
      };
      const status =
        newParentLinkDeduplicationState[entityLevel].velaris?.fieldId &&
        newParentLinkDeduplicationState[entityLevel].crm?.fieldId
          ? CONFIG_STATUS_LEVEL.COMPLETED
          : CONFIG_STATUS_LEVEL.IN_PROGRESS;
      newParentLinkDeduplicationState[entityLevel].status = status;
      return {
        ...state,
        [CONFIG_TYPES.PARENT_LINK]: newParentLinkDeduplicationState,
        hasUnSavedConfig: true,
      };
    }
    case cancelParentLinkMapping.TRIGGER: {
      const previousParentLinkMapping = cloneDeep(
        state.persistedStateParentLinkDeduplication
      );
      return {
        ...state,
        [CONFIG_TYPES.PARENT_LINK]: previousParentLinkMapping,
        hasUnSavedConfig: false,
      };
    }

    // deduplication tab - deal id mapping
    case changeAdditionalDeduplicationMapping.TRIGGER: {
      const { entityLevel, side, propertyId } = action.payload;
      const newDeduplicationState = {
        ...state[CONFIG_TYPES.DAILY_SYNC_RECORD_ID_MAPPING],
        [entityLevel]: {
          ...state[CONFIG_TYPES.DAILY_SYNC_RECORD_ID_MAPPING][entityLevel],
          [side]: { fieldId: propertyId },
        },
      };
      const status = newDeduplicationState[entityLevel].velaris.fieldId
        ? CONFIG_STATUS_LEVEL.COMPLETED
        : CONFIG_STATUS_LEVEL.IN_PROGRESS;
      newDeduplicationState[entityLevel].status = status;
      return {
        ...state,
        [CONFIG_TYPES.DAILY_SYNC_RECORD_ID_MAPPING]: newDeduplicationState,
        hasUnSavedConfig: true,
      };
    }
    case cancelAdditionalDeduplicationMapping.TRIGGER: {
      const previousDeduplicationMapping = cloneDeep(
        state.persistedStateRecordIdMapping
      );
      return {
        ...state,
        [CONFIG_TYPES.DAILY_SYNC_RECORD_ID_MAPPING]:
          previousDeduplicationMapping,
        hasUnSavedConfig: false,
      };
    }
    case fetchAdditionalDeduplicationMapping.SUCCESS: {
      const transformedDeduplication = transformEntityMappings(action.payload);
      return {
        ...state,
        [CONFIG_TYPES.DAILY_SYNC_RECORD_ID_MAPPING]: transformedDeduplication,
        persistedStateRecordIdMapping: transformedDeduplication,
        loading: false,
        error: false,
        hasUnSavedConfig: false,
      };
    }
    case createAdditionalDeduplicationMapping.SUCCESS: {
      const {
        entity: entityLevel,
        mapping: [{ velaris, crm }],
      } = action.payload;
      const deduplicationConfig = {
        ...state[CONFIG_TYPES.DAILY_SYNC_RECORD_ID_MAPPING],
      };
      deduplicationConfig[entityLevel] = {
        velaris,
        crm: { fieldId: crm.fieldId },
        crmPropertyType: { fieldId: crm.propertyType },
        status: CONFIG_STATUS_LEVEL.SAVED,
      };
      return {
        ...state,
        [CONFIG_TYPES.DAILY_SYNC_RECORD_ID_MAPPING]: deduplicationConfig,
        persistedStateRecordIdMapping: deduplicationConfig,
        loading: false,
        error: false,
        hasUnSavedConfig: false,
      };
    }
    default:
      return state;
  }
};

export default crmDataMappingReducer;
